import { useEffect, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	IconButton,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import { Add, Close, Delete } from '@mui/icons-material'
import { EMPTY_CUSTOMER_FORM, EMPTY_SENSOR_FORM } from './admin-constants'

export function CustomerFormDialog({ open, onClose, onSubmit, initial, loading, customers = [] }) {
	const isEdit = !!initial?.id
	const [form, setForm] = useState(EMPTY_CUSTOMER_FORM)
	const [sensors, setSensors] = useState([EMPTY_SENSOR_FORM])
	const [nameError, setNameError] = useState('')
	const [submitAttempted, setSubmitAttempted] = useState(false)

	useEffect(() => {
		if (open) {
			const initialUrl = initial?.webhook_url || ''
			setForm(initial ? {
				name: initial.name || '',
				webhook_url: initialUrl,
				webhook_api_key: initial.webhook_api_key || '',
				webhook_enabled: initialUrl.trim() ? (initial.webhook_enabled ?? false) : false,
				show_branding: initial.show_branding ?? true,
				custom_logo: initial.custom_logo || '',
			} : EMPTY_CUSTOMER_FORM)
			setSensors([EMPTY_SENSOR_FORM])
			setNameError('')
			setSubmitAttempted(false)
		}
	}, [open, initial])

	const allAssignedSensorIds = useMemo(() => {
		const ids = new Set()
		customers.forEach(c => {
			if (isEdit && c.id === initial?.id) return
			c.sensor_configs?.forEach(s => ids.add(s.sensor_id.trim().toLowerCase()))
		})
		return ids
	}, [customers, isEdit, initial?.id])

	const sensorErrors = useMemo(() => {
		if (isEdit) return []
		const seen = new Set()
		return sensors.map(s => {
			const id = s.sensor_id.trim().toLowerCase()
			if (!id) return ''
			if (allAssignedSensorIds.has(id)) {
				return 'This sensor ID is already assigned to another customer'
			}
			if (seen.has(id)) {
				return 'Duplicate sensor ID in this form'
			}
			seen.add(id)
			return ''
		})
	}, [sensors, allAssignedSensorIds, isEdit])

	const hasSensorErrors = sensorErrors.some(Boolean)

	const handleSubmit = (e) => {
		e.preventDefault()
		setSubmitAttempted(true)
		if (!form.name.trim()) {
			setNameError('Customer name is required')
			return
		}
		if (hasSensorErrors) return
		onSubmit(form, isEdit ? null : sensors.filter(s => s.sensor_id.trim()))
	}

	const updateSensor = (index, field, value) => {
		setSensors(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s))
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ pb: 1.5 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="h6" fontWeight="bold">
						{isEdit ? `Edit ${initial?.name}` : 'New Customer'}
					</Typography>
					<IconButton onClick={onClose} size="small">
						<Close />
					</IconButton>
				</Box>
			</DialogTitle>
			<Divider />
			<Box component="form" onSubmit={handleSubmit}>
				<DialogContent sx={{ pt: 2.5, pb: 1 }}>
					<Stack spacing={3}>
						<Box>
							<Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1 }}>
								Account Details
							</Typography>
					<TextField
							fullWidth
							label="Customer Name"
							value={form.name}
							onChange={(e) => {
								setForm({ ...form, name: e.target.value })
								if (e.target.value.trim()) setNameError('')
							}}
							required
							size="small"
							error={!!nameError}
							helperText={nameError}
						/>
						</Box>

						<Divider />

						<Box>
							<Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1 }}>
								Webhook Configuration
							</Typography>
							<Stack spacing={2}>
								<TextField
									fullWidth
									label="Webhook URL"
									type="url"
									value={form.webhook_url}
									onChange={(e) => {
										const next = e.target.value
										setForm((prev) => ({
											...prev,
											webhook_url: next,
											webhook_enabled: next.trim() ? prev.webhook_enabled : false,
										}))
									}}
									size="small"
								/>
								<TextField
									fullWidth
									label="Webhook API Key"
									value={form.webhook_api_key}
									onChange={(e) => setForm({ ...form, webhook_api_key: e.target.value })}
									size="small"
								/>
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={form.webhook_enabled}
											disabled={!form.webhook_url.trim()}
											onChange={(e) => setForm({ ...form, webhook_enabled: e.target.checked })}
										/>
									}
									label={
										<Typography variant="body2" color={form.webhook_url.trim() ? 'text.primary' : 'text.disabled'}>
											Enable webhooks{!form.webhook_url.trim() && ' (requires Webhook URL)'}
										</Typography>
									}
								/>
							</Stack>
						</Box>

						<Divider />

						<Box>
							<Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1, lineHeight: 1 }}>
								Branding
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={form.show_branding}
										onChange={(e) => setForm({ ...form, show_branding: e.target.checked })}
									/>
								}
								label={<Typography variant="body2">Show Aquatic Labs branding</Typography>}
							/>
							<TextField
								fullWidth
								size="small"
								label="Custom logo filename"
								placeholder="e.g. omen.svg"
								value={form.custom_logo}
								onChange={(e) => setForm({ ...form, custom_logo: e.target.value })}
								helperText="Asset in frontend/src/assets/customer-logos/. Rendered next to the Aquatic Labs icon."
								sx={{ mt: 1.5 }}
							/>
						</Box>

						{!isEdit && (
							<>
								<Divider />
								<Box>
									<Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1 }}>
										Initial Sensors (optional)
									</Typography>
						<Stack spacing={1.5}>
									{sensors.map((sensor, index) => (
										<Box key={index}>
											<Box display="flex" gap={1.5} alignItems="center">
												<TextField
													label="Sensor ID"
													value={sensor.sensor_id}
													onChange={(e) => updateSensor(index, 'sensor_id', e.target.value)}
													size="small"
													sx={{ flex: 1 }}
													error={submitAttempted && !!sensorErrors[index]}
												/>
												<TextField
													label="Location"
													value={sensor.sensor_location}
													onChange={(e) => updateSensor(index, 'sensor_location', e.target.value)}
													size="small"
													sx={{ flex: 1 }}
												/>
												{sensors.length > 1 && (
													<IconButton
														size="small"
														color="error"
														onClick={() => setSensors(prev => prev.filter((_, i) => i !== index))}
													>
														<Delete fontSize="small" />
													</IconButton>
												)}
											</Box>
											{submitAttempted && sensorErrors[index] && (
												<Alert severity="error" sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
													{sensorErrors[index]}
												</Alert>
											)}
										</Box>
									))}
										<Button
											size="small"
											startIcon={<Add />}
											onClick={() => setSensors(prev => [...prev, EMPTY_SENSOR_FORM])}
											sx={{ alignSelf: 'flex-start' }}
										>
											Add sensor
										</Button>
									</Stack>
								</Box>
							</>
						)}
					</Stack>
				</DialogContent>
				<Divider sx={{ mt: 2 }} />
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
					<Button type="submit" variant="contained" size="small" disabled={loading}>
						{loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Customer'}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	)
}
